import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ArrowsRightLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  BanknotesIcon,
  CalculatorIcon,
  FunnelIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  XCircleIcon as XCircleIconSolid
} from '@heroicons/react/24/solid';
import { getImageUrl } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

// استيراد الخدمات الجديدة
import { exchangeRequestsAPI, exchangeFeesAPI, subscriptionAPI } from '../../config/api';
import paymentService from '../../services/paymentService';

const ExchangeOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [exchangeCredits, setExchangeCredits] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  useEffect(() => {
    loadOffers();
    loadSubscriptionData();
  }, [filters]);

  const loadSubscriptionData = async () => {
    try {
      const [subscriptionResponse, creditsResponse] = await Promise.all([
        subscriptionAPI.getStatus(),
        subscriptionAPI.getExchangeCredits()
      ]);
      
      setSubscriptionData(subscriptionResponse.data);
      setExchangeCredits(creditsResponse.data);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      // لا نعرض toast error للاشتراك لأنه ليس ضروري للوظيفة الأساسية
    }
  };

  const loadOffers = async () => {
    try {
      setLoading(true);
      const params = {
        type: 'received', // طلبات واردة فقط
        status: filters.status === 'all' ? undefined : filters.status,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await exchangeRequestsAPI.getExchangeRequests(params);
      // التأكد من أن البيانات array
      const offersData = response.data?.requests || response.data?.data || response.data || [];
      console.log('Loaded offers:', offersData);
      setOffers(Array.isArray(offersData) ? offersData : []);
    } catch (error) {
      console.error('Error loading offers:', error);
      setOffers([]); // تعيين array فارغ في حالة الخطأ
      // لا نعرض toast error إذا كانت المشكلة أن API غير موجود
      if (error.message && !error.message.includes('404')) {
        toast.error('فشل في تحميل طلبات التبادل');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (offer) => {
    try {
      const response = await exchangeRequestsAPI.getExchangeRequest(offer._id);
      setSelectedOffer(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('فشل في تحميل تفاصيل العرض');
    }
  };

  const handleAcceptOffer = async (offer) => {
    try {
      setPaymentLoading(true);
      
      // حساب رسوم التبادل
      const feeResponse = await exchangeFeesAPI.calculateFee({
        exchangeRequestId: offer._id
      });
      
      console.log('Fee calculation response:', feeResponse);
      
      // استجابة الباكند تكون بالشكل { success, message, timestamp, data }
      const feeData = feeResponse?.data?.data;
      if (feeData) {
        setFeeCalculation(feeData);
        setSelectedOffer(offer);
        setShowDetailsModal(false);
        setShowPaymentModal(true);
      } else {
        toast.error('فشل في حساب رسوم التبادل - بيانات غير كاملة');
      }
      
    } catch (error) {
      toast.error('فشل في حساب رسوم التبادل');
      console.error('Error calculating fees:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleRejectOffer = async (offer, reason) => {
    try {
      await exchangeRequestsAPI.respondToRequest(offer._id, {
        action: 'reject',
        rejectionReason: reason
      });
      
      toast.success('تم رفض العرض');
      setShowDetailsModal(false);
      loadOffers();
    } catch (error) {
      toast.error('فشل في رفض العرض');
    }
  };

  const proceedToPayment = async () => {
    try {
      setPaymentLoading(true);
      
      // بدء عملية الدفع
      const paymentResponse = await exchangeFeesAPI.initiatePayment({
        exchangeRequestId: selectedOffer._id
      });
      
      console.log('Payment response:', paymentResponse);
      
      const responseData = paymentResponse?.data?.data || {};
      
      // التحقق إذا كان التبادل مجاني (استخدم رصيد)
      if (responseData.feePayment?.isFreeCredit) {
        // تبادل مجاني - إغلاق النموذج وإظهار رسالة نجاح
        toast.success('🎉 تم قبول التبادل مجاناً باستخدام رصيد بديل بلس!');
        toast.success(`الرصيد المتبقي: ${responseData.remainingCredits || 0} تبادلات`);
        
        if (responseData.exchangeTransaction) {
          toast.success(`رقم معاملة التبادل: ${responseData.exchangeTransaction.transactionNumber}`);
        }
        
        setShowPaymentModal(false);
        setSelectedOffer(null);
        setFeeCalculation(null);
        setPaymentLoading(false);
        
        // إعادة تحميل العروض والاشتراك
        loadOffers();
        loadSubscriptionData();
        
        return;
      }
      
      // إعداد نموذج الدفع (Moyasar) للتبادلات المدفوعة
      const { moyasarData } = responseData;
      
      if (!moyasarData) {
        throw new Error('بيانات الدفع غير مكتملة');
      }
      
      // تفريغ الحاوية وإنشاء نموذج الدفع بعد تحميل سكربت Moyasar
      const container = document.querySelector('.exchange-payment-form');
      if (container) container.innerHTML = '';

      await paymentService.initializeMoyasar('.exchange-payment-form', {
        publishable_api_key: moyasarData.publishable_api_key,
        amount: moyasarData.amount,
        currency: moyasarData.currency,
        description: moyasarData.description,
        callback_url: `${window.location.origin}/lab/exchange-success/${responseData.feePayment?._id}`,
        methods: ['creditcard'],
        metadata: moyasarData.metadata
      });
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('فشل في بدء عملية الدفع');
      setPaymentLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        icon: ClockIcon, 
        text: 'في الانتظار', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200' 
      },
      viewed: { 
        icon: EyeIcon, 
        text: 'تم العرض - يمكن قبوله', 
        color: 'bg-blue-100 text-blue-800 border-blue-200' 
      },
      accepted: { 
        icon: CheckCircleIcon, 
        text: 'مقبول', 
        color: 'bg-green-100 text-green-800 border-green-200' 
      },
      rejected: { 
        icon: XCircleIcon, 
        text: 'مرفوض', 
        color: 'bg-red-100 text-red-800 border-red-200' 
      },
      expired: { 
        icon: ExclamationTriangleIcon, 
        text: 'منتهي الصلاحية', 
        color: 'bg-gray-100 text-gray-800 border-gray-200' 
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${config.color}`}>
        <Icon className="w-4 h-4 ml-1" />
        {config.text}
      </span>
    );
  };

  const renderOfferCard = (offer) => (
    <motion.div
      key={offer._id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 hover:border-brand-blue/30 group"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <ArrowsRightLeftIcon className="w-6 h-6 text-brand-blue" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-brand-blue transition-colors">طلب تبادل</h3>
              <p className="text-sm text-gray-500">#{offer.requestNumber}</p>
            </div>
          </div>
          {getStatusBadge(offer.status)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
          {/* المنتج المطلوب */}
          <div className="bg-gradient-to-br from-brand-blue/5 via-brand-blue/10 to-brand-blue/5 rounded-xl p-4 border border-brand-blue/20">
            <h4 className="font-medium text-brand-blue mb-3 flex items-center">
              <SparklesIcon className="w-4 h-4 ml-1" />
              منتجك المطلوب
            </h4>
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={getImageUrl(offer.targetProduct?.product?.images?.[0]?.url)}
                  alt={offer.targetProduct?.product?.name}
                  className="w-16 h-16 object-cover rounded-xl border-2 border-brand-blue/30 shadow-sm"
                />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-brand-blue rounded-full flex items-center justify-center">
                  <SparklesIcon className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 mb-1">
                  {offer.targetProduct?.product?.name}
                </h5>
                <p className="text-sm text-gray-600">
                  الكمية: {offer.targetProduct?.requestedQuantity}
                </p>
                {offer.targetProduct?.product?.actualValue && (
                  <p className="text-sm font-medium text-brand-blue">
                    القيمة: {offer.targetProduct.product.actualValue.amount.toLocaleString()} ريال
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* المنتج المعروض */}
          <div className="bg-gradient-to-br from-brand-blue/5 via-brand-green/5 to-brand-blue/5 rounded-xl p-4 border border-brand-blue/20">
            <h4 className="font-medium text-brand-blue mb-3 flex items-center">
              <ArrowsRightLeftIcon className="w-4 h-4 ml-1" />
              العرض المقدم
            </h4>
            {offer.offerType === 'existing_product' ? (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src={getImageUrl(offer.offeredProduct?.product?.images?.[0]?.url)}
                    alt={offer.offeredProduct?.product?.name}
                    className="w-16 h-16 object-cover rounded-xl border-2 border-brand-blue/30 shadow-sm"
                  />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-brand-blue to-brand-green rounded-full flex items-center justify-center">
                    <ArrowsRightLeftIcon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 mb-1">
                    {offer.offeredProduct?.product?.name}
                  </h5>
                  <p className="text-sm text-gray-600">
                    الكمية: {offer.offeredProduct?.quantity}
                  </p>
                  {offer.offeredProduct?.product?.actualValue && (
                    <p className="text-sm font-medium text-brand-blue">
                      القيمة: {offer.offeredProduct.product.actualValue.amount.toLocaleString()} ريال
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-blue to-brand-green rounded-xl flex items-center justify-center shadow-sm">
                    <BanknotesIcon className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-brand-blue to-brand-green rounded-full flex items-center justify-center">
                    <SparklesIcon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900 mb-1">
                    {offer.customOffer?.name}
                  </h5>
                  <p className="text-sm text-gray-600">
                    الكمية: {offer.customOffer?.quantity}
                  </p>
                  <p className="text-sm font-medium text-brand-blue">
                    القيمة المقدرة: {offer.customOffer?.estimatedValue?.toLocaleString()} ريال
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* معلومات إضافية */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">{new Date(offer.createdAt).toLocaleDateString('ar-SA')}</span>
            </div>
          </div>
        </div>

        {/* الأزرار */}
        <div className="flex gap-3">
          <button
            onClick={() => handleViewDetails(offer)}
            className="flex-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 flex items-center justify-center font-medium shadow-sm hover:shadow-md"
          >
            <EyeIcon className="w-4 h-4 ml-1" />
            عرض التفاصيل
          </button>
          
          {(offer.status === 'pending' || offer.status === 'viewed') && (
            <>
              <button
                onClick={() => handleAcceptOffer(offer)}
                disabled={paymentLoading}
                className="flex-1 bg-gradient-to-r from-brand-blue to-brand-green text-white py-3 px-4 rounded-xl hover:from-brand-blueSecondary hover:to-brand-blue transition-all duration-200 flex items-center justify-center font-medium disabled:opacity-50 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
              >
                <CheckCircleIcon className="w-4 h-4 ml-1" />
                {paymentLoading ? 'جاري المعالجة...' : 'قبول العرض'}
              </button>
              <button
                onClick={() => handleRejectOffer(offer, 'تم الرفض من قبل المختبر')}
                className="px-4 py-3 bg-gradient-to-r from-red-100 to-red-200 text-red-600 rounded-xl hover:from-red-200 hover:to-red-300 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderDetailsModal = () => {
    if (!showDetailsModal || !selectedOffer) {
      return null;
    }

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDetailsModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-2xl relative overflow-hidden">
              <div className="absolute inset-0 bg-white opacity-5">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  <pattern id="details-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="currentColor" />
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#details-pattern)" />
                </svg>
              </div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                    <EyeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">تفاصيل طلب التبادل</h2>
                    <p className="text-blue-100">#{selectedOffer.requestNumber}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <XCircleIcon className="w-6 h-6 text-white/80" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* معلومات المختبر المرسل */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <BuildingOfficeIcon className="w-5 h-5 ml-2 text-brand-blue" />
                  معلومات المختبر المرسل
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">اسم المختبر:</p>
                    <p className="font-medium text-brand-blue">{selectedOffer.requesterLab?.labName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">تاريخ الطلب:</p>
                    <p className="font-medium text-gray-900">{new Date(selectedOffer.createdAt).toLocaleDateString('ar-SA')}</p>
                  </div>
                </div>
              </div>

              {/* تفاصيل المنتجات */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* المنتج المطلوب */}
                <div className="bg-gradient-to-br from-brand-blue/5 via-brand-blue/10 to-brand-blue/5 rounded-xl p-4 border border-brand-blue/20">
                  <h4 className="font-medium text-brand-blue mb-3 flex items-center">
                    <SparklesIcon className="w-4 h-4 ml-1" />
                    منتجك المطلوب
                  </h4>
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <img
                        src={getImageUrl(selectedOffer.targetProduct?.product?.images?.[0]?.url)}
                        alt={selectedOffer.targetProduct?.product?.name}
                        className="w-20 h-20 object-cover rounded-xl border-2 border-brand-blue/30 shadow-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 mb-2">
                        {selectedOffer.targetProduct?.product?.name}
                      </h5>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-600">الكمية المطلوبة:</span> <span className="font-medium">{selectedOffer.targetProduct?.requestedQuantity}</span></p>
                        <p><span className="text-gray-600">الفئة:</span> <span className="font-medium">{selectedOffer.targetProduct?.product?.category?.name}</span></p>
                        {selectedOffer.targetProduct?.product?.actualValue && (
                          <p><span className="text-gray-600">القيمة:</span> <span className="font-medium text-brand-blue">{selectedOffer.targetProduct.product.actualValue.amount.toLocaleString()} ريال</span></p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* المنتج المعروض */}
                <div className="bg-gradient-to-br from-brand-blue/5 via-brand-green/5 to-brand-blue/5 rounded-xl p-4 border border-brand-blue/20">
                  <h4 className="font-medium text-brand-blue mb-3 flex items-center">
                    <ArrowsRightLeftIcon className="w-4 h-4 ml-1" />
                    العرض المقدم
                  </h4>
                  {selectedOffer.offerType === 'existing_product' ? (
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <img
                          src={getImageUrl(selectedOffer.offeredProduct?.product?.images?.[0]?.url)}
                          alt={selectedOffer.offeredProduct?.product?.name}
                          className="w-20 h-20 object-cover rounded-xl border-2 border-brand-blue/30 shadow-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 mb-2">
                          {selectedOffer.offeredProduct?.product?.name}
                        </h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-gray-600">الكمية المعروضة:</span> <span className="font-medium">{selectedOffer.offeredProduct?.quantity}</span></p>
                          <p><span className="text-gray-600">الفئة:</span> <span className="font-medium">{selectedOffer.offeredProduct?.product?.category?.name}</span></p>
                          {selectedOffer.offeredProduct?.product?.actualValue && (
                            <p><span className="text-gray-600">القيمة:</span> <span className="font-medium text-brand-blue">{selectedOffer.offeredProduct.product.actualValue.amount.toLocaleString()} ريال</span></p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <div className="w-20 h-20 bg-gradient-to-br from-brand-blue to-brand-green rounded-xl flex items-center justify-center shadow-sm">
                        <BanknotesIcon className="w-10 h-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900 mb-2">
                          {selectedOffer.customOffer?.name}
                        </h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-gray-600">الكمية:</span> <span className="font-medium">{selectedOffer.customOffer?.quantity}</span></p>
                          <p><span className="text-gray-600">القيمة المقدرة:</span> <span className="font-medium text-brand-blue">{selectedOffer.customOffer?.estimatedValue?.toLocaleString()} ريال</span></p>
                          {selectedOffer.customOffer?.description && (
                            <p><span className="text-gray-600">الوصف:</span> <span className="font-medium">{selectedOffer.customOffer.description}</span></p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ملاحظات إضافية */}
              {selectedOffer.notes && (
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 mb-6 border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                    <InformationCircleIcon className="w-4 h-4 ml-1" />
                    ملاحظات الطلب
                  </h4>
                  <p className="text-blue-800">{selectedOffer.notes}</p>
                </div>
              )}

              {/* الأزرار */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 py-3 px-6 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  إغلاق
                </button>
                {(selectedOffer.status === 'pending' || selectedOffer.status === 'viewed') && (
                  <>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleAcceptOffer(selectedOffer);
                      }}
                      className="flex-1 bg-gradient-to-r from-brand-blue to-brand-green text-white py-3 px-6 rounded-xl hover:from-brand-blueSecondary hover:to-brand-blue transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                      قبول العرض
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleRejectOffer(selectedOffer, 'تم الرفض من قبل المختبر');
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-red-100 to-red-200 text-red-600 rounded-xl hover:from-red-200 hover:to-red-300 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                    >
                      رفض العرض
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  const renderPaymentModal = () => {
    // التحقق من وجود البيانات المطلوبة
    if (!showPaymentModal || !feeCalculation || !selectedOffer) {
      return null;
    }

    return (
      <AnimatePresence>
        {(
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPaymentModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-white opacity-5">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <pattern id="payment-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                      <circle cx="20" cy="20" r="1" fill="currentColor" />
                    </pattern>
                    <rect width="100%" height="100%" fill="url(#payment-pattern)" />
                  </svg>
                </div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                      <CurrencyDollarIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">دفع رسوم التبادل</h2>
                      <p className="text-blue-100">اكمل الدفع لتأكيد العرض وبدء التبادل</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <XCircleIcon className="w-6 h-6 text-white/80" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">

              {/* معلومات الاشتراك والرصيد */}
              {subscriptionData?.subscription?.isActive && (
                <div className="bg-gradient-to-r from-brand-blue/10 via-brand-green/10 to-brand-blue/10 rounded-xl p-6 mb-6 border-2 border-brand-blue/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-r from-brand-blue to-brand-green rounded-lg">
                      <SparklesIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-brand-blue">مشترك بديل بلس 🎉</h3>
                      <p className="text-sm text-brand-blue/80">استمتع بمزايا الاشتراك المتميز</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-brand-blue/20">
                      <div className="flex items-center gap-2 mb-2">
                        <BanknotesIcon className="w-5 h-5 text-brand-blue" />
                        <span className="font-medium text-gray-900">رصيد التبادلات المجانية</span>
                      </div>
                      <div className="text-2xl font-bold text-brand-blue">
                        {exchangeCredits?.exchangeCredits?.available || 0}
                      </div>
                      <p className="text-sm text-gray-600">
                        تبادل متبقي من أصل 10 سنوياً
                      </p>
                    </div>

                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-brand-blue/20">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircleIconSolid className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-gray-900">حالة هذا التبادل</span>
                      </div>
                      {exchangeCredits?.exchangeCredits?.available > 0 ? (
                        <>
                          <div className="text-lg font-bold text-green-600 mb-1">
                            مجاني! 🎉
                          </div>
                          <p className="text-sm text-green-700">
                            سيتم خصم تبادل واحد من رصيدك
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-bold text-orange-600 mb-1">
                            رسوم مخفضة
                          </div>
                          <p className="text-sm text-orange-700">
                            تم استنفاد الرصيد المجاني
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* تفاصيل الرسوم */}
              <div className="bg-gradient-to-br from-brand-blue/5 via-brand-green/5 to-brand-blue/10 rounded-xl p-6 mb-6 border border-brand-blue/20">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-brand-blue/10 to-brand-green/10 rounded-lg">
                    <CalculatorIcon className="w-6 h-6 text-brand-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">تفصيل الرسوم</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">منتجك</p>
                    <p className="font-medium">
                      {feeCalculation.breakdown?.productA?.name || 
                       feeCalculation.breakdown?.targetProduct?.name || 
                       selectedOffer?.targetProduct?.product?.name || 'منتج غير محدد'}
                    </p>
                    <p className="text-sm text-purple-600">
                      {(feeCalculation.breakdown?.productA?.totalValue || 
                        feeCalculation.breakdown?.targetProduct?.totalValue || 0)?.toLocaleString()} ريال
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">المنتج المعروض</p>
                    <p className="font-medium">
                      {feeCalculation.breakdown?.productB?.name || 
                       feeCalculation.breakdown?.customOffer?.name ||
                       selectedOffer?.offeredProduct?.product?.name ||
                       selectedOffer?.customOffer?.name || 'منتج غير محدد'}
                    </p>
                    <p className="text-sm text-purple-600">
                      {(feeCalculation.breakdown?.productB?.totalValue || 
                        feeCalculation.breakdown?.customOffer?.totalValue || 0)?.toLocaleString()} ريال
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">إجمالي قيمة التبادل:</span>
                    <span className="font-medium">{(feeCalculation.totalValue || 0).toLocaleString()} ريال</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">شريحة الرسوم:</span>
                    <span className="text-sm text-gray-500">{feeCalculation.feeBracket || 'غير محدد'}</span>
                  </div>
                  
                  {/* عرض الرسوم بناءً على حالة الاشتراك والرصيد */}
                  {subscriptionData?.subscription?.isActive && exchangeCredits?.exchangeCredits?.available > 0 ? (
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <div className="flex justify-between items-center text-lg font-bold text-green-700">
                        <span className="flex items-center gap-2">
                          <CheckCircleIconSolid className="w-5 h-5" />
                          رسوم التبادل:
                        </span>
                        <div className="text-right">
                          <div className="line-through text-sm text-gray-500">
                            {(feeCalculation.feeAmount || 0).toLocaleString()} ريال
                          </div>
                          <div className="text-green-700 font-bold">
                            مجاني! 🎉
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-green-600 mt-2">
                        ✨ تم تطبيق ميزة بديل بلس - تبادل مجاني من رصيدك
                      </p>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center text-lg font-bold text-brand-blue">
                      <span>رسوم التبادل:</span>
                      <span>{(feeCalculation.feeAmount || 0).toLocaleString()} ريال</span>
                      {subscriptionData?.subscription?.isActive && (
                        <div className="text-xs text-orange-600 ml-2">
                          (رصيد التبادلات المجانية مستنفد)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* تحذير */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">ملاحظة مهمة:</p>
                    <p>بعد التأكيد سيتم خصم الكميات من المنتجات وإنشاء معاملة التبادل.</p>
                    {subscriptionData?.subscription?.isActive && exchangeCredits?.exchangeCredits?.available > 0 && (
                      <p className="mt-1 text-green-700 font-medium">
                        ✨ سيتم خصم تبادل واحد من رصيد التبادلات المجانية الخاص بك
                      </p>
                    )}
                    <p className="mt-1">لا يمكن التراجع بعد التأكيد.</p>
                  </div>
                </div>
              </div>

              {/* نموذج الدفع - يظهر فقط للتبادلات المدفوعة */}
              {(!subscriptionData?.subscription?.isActive || !exchangeCredits?.exchangeCredits?.available || exchangeCredits?.exchangeCredits?.available <= 0) && (
                <div className="exchange-payment-form mb-4"></div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 py-3 px-6 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                >
                  إلغاء
                </button>
                <button
                  onClick={proceedToPayment}
                  disabled={paymentLoading}
                  className="flex-1 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white py-3 px-6 rounded-xl hover:opacity-95 transition-all duration-200 disabled:opacity-50 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {paymentLoading ? 'جاري التحضير...' : 
                   subscriptionData?.subscription?.isActive && exchangeCredits?.exchangeCredits?.available > 0 
                   ? 'تأكيد التبادل المجاني' 
                   : 'متابعة الدفع'}
                </button>
              </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
    );
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ArrowsRightLeftIcon className="h-8 w-8 ml-3 text-brand-blue" />
                طلبات التبادل الواردة
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                إدارة جميع العروض المقدمة على منتجاتك من المختبرات الأخرى
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* الفلاتر والإحصائيات */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FunnelIcon className="w-5 h-5 text-brand-blue" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">تصفية العروض</h2>
          </div>
          <div className="text-sm text-gray-500">
            إجمالي العروض: <span className="font-medium text-brand-blue">{offers.length}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">حالة العرض</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-colors"
            >
              <option value="all">جميع الحالات</option>
              <option value="pending">جديدة (في الانتظار)</option>
              <option value="viewed">تم العرض (قابلة للقبول)</option>
              <option value="accepted">مقبولة</option>
              <option value="rejected">مرفوضة</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">الترتيب</label>
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                setFilters(prev => ({ ...prev, sortBy, sortOrder }));
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-colors"
            >
              <option value="createdAt-desc">الأحدث أولاً</option>
              <option value="createdAt-asc">الأقدم أولاً</option>
            </select>
          </div>
          
          <div className="md:col-span-2 lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">إحصائيات سريعة</label>
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-3 text-center border border-yellow-200">
                <div className="text-lg font-bold text-yellow-700">
                  {offers.filter(o => o.status === 'pending').length}
                </div>
                <div className="text-xs text-yellow-600">جديدة</div>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 text-center border border-blue-200">
                <div className="text-lg font-bold text-blue-700">
                  {offers.filter(o => o.status === 'viewed').length}
                </div>
                <div className="text-xs text-blue-600">تم العرض</div>
              </div>
              <div className="bg-gradient-to-r from-brand-blue/10 to-brand-green/10 rounded-lg p-3 text-center border border-brand-blue/20">
                <div className="text-lg font-bold text-brand-blue">
                  {offers.filter(o => o.status === 'accepted').length}
                </div>
                <div className="text-xs text-brand-blue">مقبول</div>
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 text-center border border-gray-200">
                <div className="text-lg font-bold text-gray-700">
                  {offers.filter(o => o.status === 'rejected').length}
                </div>
                <div className="text-xs text-gray-600">مرفوض</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* قائمة العروض */}
      <div className="grid grid-cols-1 gap-6">
        {offers.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center"
          >
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-brand-blue/10 to-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <ArrowsRightLeftIcon className="w-12 h-12 text-brand-blue" />
              </div>
              <div className="absolute top-0 right-1/2 transform translate-x-1/2 w-6 h-6 bg-gradient-to-r from-brand-blue to-brand-green rounded-full flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">لا توجد طلبات تبادل واردة</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">لم يتم تقديم أي عروض على منتجاتك بعد. تأكد من إضافة منتجات للتبادل لتلقي العروض.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.href = '/lab/products'}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-xl hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
              >
                <CubeIcon className="w-5 h-5 ml-2" />
                إدارة المنتجات
              </button>
              <button
                onClick={() => window.location.href = '/products'}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              >
                <EyeIcon className="w-5 h-5 ml-2" />
                تصفح المتجر
              </button>
            </div>
          </motion.div>
        ) : (
          offers.map(renderOfferCard)
        )}
      </div>

      {/* نموذج التفاصيل */}
      {renderDetailsModal()}

      {/* نموذج الدفع */}
      {renderPaymentModal()}
    </div>
  );
};

export default ExchangeOffers;
